// @ts-nocheck
/*
 * Call-Centre user create — v1 logic preserved end-to-end (state, API
 * sequence, validation, toasts). UI ported off react-bootstrap onto
 * shadcn to end the two-UI-library situation in the Users flow.
 *
 * v1 note preserved: <RenderUserAccessRolesList activeUserAccessRole=...>
 * was passed without userAccessRoles/setActiveUserAccessRole, so its
 * internal guard always renders <></>. Kept verbatim.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Loader2, ArrowLeft } from 'lucide-react';
import {
  API_USERS_SIGNUP,
  API_SIGNUP_EMAIL_SEND_CODE,
  API_SIGNUP_EMAIL_VERIFY_CODE,
} from '@/service/url';
import { postApiCall } from '@/service/httpCall';
import RenderUserAccessRolesList from '@/components/partials/components/user-access-roles-list';
import { getUserCreateAccessRoleLabel, Storage } from '@/utilities/setting';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import { ROUTES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { validateEmail } from '@/utilities/helpers';
import {
  AvailabilityHint,
  useUsernameAvailability,
  useUserEmailAvailability,
} from '@/hooks/useAvailability';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { DEFAULT_AGENT_LEADS, parseAgentLeadsInput, currentUserHasCustomAgentLeads } from '@/utilities/agentLeadsQuota';

const cx = (...args) => args.filter(Boolean).join(' ');

const CallCentreUserCreate = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { checkAuth } = useAuth();

  const [callCentreId, setCallCentreId] = useState(undefined);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [buttonDisable, setButtonDisable] = useState(false);
  const [activeUserAccessRole, setActiveUserAccessRole] = useState(undefined);
  const [, setUserAccessRoles] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [nameError, setNameError] = useState('');
  const [signupEmailOtp, setSignupEmailOtp] = useState('');
  const [signupEmailToken, setSignupEmailToken] = useState('');
  const [emailVerifiedForCreate, setEmailVerifiedForCreate] = useState(false);
  const [signupEmailBusy, setSignupEmailBusy] = useState(false);
  const [agentLeads, setAgentLeads] = useState(String(DEFAULT_AGENT_LEADS));
  const canConfigureLeads = useMemo(() => currentUserHasCustomAgentLeads(), []);

  const [, setShowToast] = useState(false);

  const USERNAME_PATTERN = /^[A-Za-z0-9_]{3,50}$/;
  const usernameStatus = useUsernameAvailability(username, {
    enabled: USERNAME_PATTERN.test(username.trim()),
  });
  const emailStatus = useUserEmailAvailability(email, {
    enabled: Boolean(email.trim()) && !validateEmail(email),
  });
  const [, setToastProps] = useState(null);

  const emailVerificationRequired = useMemo(() => {
    try {
      const creatorUser = JSON.parse(Storage.getItem('user') || 'null');
      return creatorRequiresEmailVerificationOnUserForms(creatorUser, {
        userRole: creatorUser?.role,
      });
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    setCallCentreId(params.id);
    getUserCreateAccessRoleLabel()
      .then((resp) => {
        if (resp?.length) {
          setUserAccessRoles(resp);
          setActiveUserAccessRole(resp[0].value);
        }
      })
      .catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendSignupEmailCode() {
    if (!email.trim() || validateEmail(email)) {
      toast.error('Enter a valid email before sending a code.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      await postApiCall(API_SIGNUP_EMAIL_SEND_CODE, { email: email.trim() });
      setSignupEmailToken('');
      setEmailVerifiedForCreate(false);
      toast.success('Verification code sent. Check the inbox for this email.');
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not send verification code');
    } finally {
      setSignupEmailBusy(false);
    }
  }

  async function verifySignupEmailOtp() {
    if (!email.trim() || validateEmail(email)) {
      toast.error('Enter a valid email.');
      return;
    }
    if (!signupEmailOtp || signupEmailOtp.length !== 6) {
      toast.error('Enter the 6-digit code from the email.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      const data = await postApiCall(API_SIGNUP_EMAIL_VERIFY_CODE, {
        email: email.trim(),
        otp: signupEmailOtp.trim(),
      });
      if (data?.signupEmailToken) {
        setSignupEmailToken(data.signupEmailToken);
        setEmailVerifiedForCreate(true);
        toast.success('Email verified. You can create the user now.');
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Verification failed');
      setEmailVerifiedForCreate(false);
      setSignupEmailToken('');
    } finally {
      setSignupEmailBusy(false);
    }
  }

  function validateForm() {
    let valid = true;
    if (!validateUsername(username)) {
      valid = false;
      if (!username.trim()) toast.warning('Username required');
      else toast.warning(usernameError || 'Invalid username format');
    }
    if (!validateName(name)) {
      valid = false;
      if (!name.trim()) toast.warning('Name required');
      else toast.warning(nameError || 'Invalid name format');
    }
    if (usernameStatus === 'taken') {
      valid = false;
      toast.warning('Username already exists');
    }
    if (emailStatus === 'taken') {
      valid = false;
      toast.warning('Email already exists');
    }
    if (!email.trim()) {
      valid = false;
      toast.warning('Email required');
    } else if (!password.trim()) {
      valid = false;
      toast.warning('Password required');
    } else if (password.trim().length < 6) {
      valid = false;
      toast.warning('Password must be of minimum 6 characters');
    } else if (!confirmPassword.trim()) {
      valid = false;
      toast.warning('Confirm password required');
    } else if (password.trim() !== confirmPassword.trim()) {
      valid = false;
      toast.warning('Password and confirm password not matching');
    } else if (validateEmail(email)) {
      valid = false;
      toast.warning(validateEmail(email) || 'Invalid email');
    } else if (
      emailVerificationRequired &&
      (!emailVerifiedForCreate || !signupEmailToken)
    ) {
      valid = false;
      toast.warning('Verify this email first: Send code, enter OTP, then Verify OTP.');
    }
    if (canConfigureLeads) {
      const agentLeadsCheck = parseAgentLeadsInput(agentLeads);
      if (!agentLeadsCheck.ok) {
        valid = false;
        toast.warning(agentLeadsCheck.message);
      }
    }
    return valid;
  }

  async function submitForm() {
    setButtonDisable(true);
    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }
    const agentLeadsCheck = canConfigureLeads ? parseAgentLeadsInput(agentLeads) : null;
    const body = {
      username,
      password,
      role: activeUserAccessRole,
      name,
      email,
      ...(canConfigureLeads
        ? { agent_leads: agentLeadsCheck.ok ? agentLeadsCheck.value : DEFAULT_AGENT_LEADS }
        : {}),
      callcenterId: callCentreId,
      created_by: callCentreId,
      ...(emailVerificationRequired && signupEmailToken
        ? { signup_email_token: signupEmailToken }
        : {}),
    };
    postApiCall(API_USERS_SIGNUP, body).then(onUserCreateSuccess).catch(onUserCreateError);
  }

  function onUserCreateSuccess() {
    toast.success('User created successfully.');
    setTimeout(() => {
      const callCentreUsersListPath = ROUTES.CALL_CENTRES.USERS.LIST.replace(':id', callCentreId);
      navigate(`/${callCentreUsersListPath}`);
    }, 1500);
  }

  function onUserCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.warning(JSON.stringify(err));
    }
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const validateUsername = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return (setUsernameError('Username is required'), false);
    if (trimmedValue.length < 3 || trimmedValue.length > 50)
      return (setUsernameError('Username must be between 3 and 50 characters'), false);
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedValue))
      return (setUsernameError('Username can only contain letters, numbers, and underscores'), false);
    setUsernameError('');
    return true;
  };

  const validateName = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return (setNameError('Name is required'), false);
    if (trimmedValue.length < 2 || trimmedValue.length > 100)
      return (setNameError('Name must be between 2 and 100 characters'), false);
    if (!/^[a-zA-Z0-9_ ]+$/.test(trimmedValue))
      return (setNameError('Name can only contain letters, numbers, underscores, and spaces'), false);
    setNameError('');
    return true;
  };

  const validateEmailField = (value) => setEmailError(validateEmail(value));

  const invalidCls = 'border-destructive focus-visible:ring-destructive';

  return (
    <ScrollPage>
      <PageHeader
        title="New User Information"
        description="Create a new call-centre user under this account."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />
      <Card>
        <CardHeader>
          {/* v1 quirk preserved — always renders <></> without full props */}
          <RenderUserAccessRolesList activeUserAccessRole={activeUserAccessRole} />
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium leading-none">
                  User Name
                </label>
                <Input
                  type="text"
                  id="username"
                  maxLength={50}
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    validateUsername(e.target.value);
                  }}
                  onBlur={(e) => validateUsername(e.target.value)}
                  className={cx(usernameError && invalidCls)}
                  required
                />
                {usernameError && <p className="text-xs text-destructive">{usernameError}</p>}
                {!usernameError && (
                  <AvailabilityHint status={usernameStatus} takenMessage="Username already exists" />
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium leading-none">
                  Full Name
                </label>
                <Input
                  type="text"
                  id="name"
                  maxLength={100}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    validateName(e.target.value);
                  }}
                  onBlur={(e) => validateName(e.target.value)}
                  className={cx(nameError && invalidCls)}
                  required
                />
                {nameError && <p className="text-xs text-destructive">{nameError}</p>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium leading-none">
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  maxLength={100}
                  value={email}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEmail(v);
                    validateEmailField(v);
                    setSignupEmailToken('');
                    setSignupEmailOtp('');
                    setEmailVerifiedForCreate(false);
                  }}
                  required
                />
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                {!emailError && (
                  <AvailabilityHint status={emailStatus} takenMessage="Email already exists" />
                )}
                {emailVerificationRequired && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    {emailVerifiedForCreate ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        Email verified
                      </span>
                    ) : (
                      <>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="6-digit code"
                          maxLength={6}
                          className="max-w-[140px]"
                          value={signupEmailOtp}
                          onChange={(e) =>
                            setSignupEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                          }
                          disabled={signupEmailBusy}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={signupEmailBusy || !email.trim() || !!validateEmail(email)}
                          onClick={sendSignupEmailCode}
                        >
                          Send code
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={signupEmailBusy || signupEmailOtp.length !== 6}
                          onClick={verifySignupEmailOtp}
                        >
                          Verify OTP
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {canConfigureLeads ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="agentLeads" className="text-sm font-medium leading-none">
                  Agent leads
                </label>
                <Input
                  type="number"
                  id="agentLeads"
                  min={0}
                  step={1}
                  value={agentLeads}
                  onChange={(e) => setAgentLeads(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Shown because Leads is on the Call Centres ACL. Defaults to 100.
                </p>
              </div>
            </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="pass" className="text-sm font-medium leading-none">
                  Password
                </label>
                <Input
                  type="password"
                  id="pass"
                  maxLength={100}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="rpass" className="text-sm font-medium leading-none">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  id="rpass"
                  maxLength={100}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <hr className="border-border" />

            <Button
              onClick={() => submitForm()}
              type="button"
              disabled={buttonDisable}
              className="gap-2"
            >
              Add New User
              {buttonDisable && <Loader2 className="h-4 w-4 animate-spin" />}
            </Button>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
};

export default CallCentreUserCreate;
